import { useMemo } from "react";
import { HttpAttachment } from "tekkr-common/dist/model/attachments";
import { useProjectAttachmentsQuery } from "./queries/project-attachments";

interface UseProjectAttachmentsReturn {
    allAttachments: HttpAttachment[] | null;
    uniqueAttachments: HttpAttachment[] | null;
    getAttachments: (scopeFilter?: string[]) => HttpAttachment[];
    getAttachmentById: (id: string) => HttpAttachment | undefined;
    isLoading: boolean;
    refetch: () => Promise<void>;
}

export function useProjectAttachments(): UseProjectAttachmentsReturn {
    const { data, refetch, isLoading } = useProjectAttachmentsQuery();

    // Temporary de-duplication by name until TEK-675 allows multi-target referencing
    const uniqueAttachments = useMemo(() => {
        if (!data) return null;
        const seen = new Set<string>();
        return data.filter((a) => {
            if (seen.has(a.name)) return false;
            seen.add(a.name);
            return true;
        });
    }, [data]);

    return {
        allAttachments: data ?? null,
        uniqueAttachments,
        getAttachments: (scopeFilter?: string[]) => {
            if (!data) return [];
            if (!scopeFilter || scopeFilter.length === 0) return data;
            return data.filter((attachment) => {
                return !scopeFilter.find((s, i) => s !== attachment.target?.[i]);
            });
        },
        getAttachmentById: (id: string) => {
            if (!data) return undefined;
            return data.find((attachment) => attachment.id === id);
        },
        isLoading,
        refetch: async () => {
            await refetch();
        },
    };
}
